import { Property, PropertyFilters, PropertyStats, User } from '../types/property.types';

// Export LoginCredentials interface
export interface LoginCredentials {
  email: string;
  password: string;
}

// Debug logging para variables de entorno
console.log('🔍 Environment variables:', {
  VITE_API_URL: import.meta.env.VITE_API_URL,
  MODE: import.meta.env.MODE,
  DEV: import.meta.env.DEV,
  PROD: import.meta.env.PROD
});

// Use environment variable or fallback to the NEW DEPLOYED API URL
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://a5lg9w7zsk.execute-api.us-east-1.amazonaws.com/dev';

console.log('🌐 Using API URL:', API_BASE_URL);

class PropertyService {
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async getAll(filters?: PropertyFilters): Promise<{ propiedades: Property[]; count: number }> {
    const queryParams = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }

    const endpoint = `/properties${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.request<{ propiedades: Property[]; count: number }>(endpoint);
  }

  async getById(id: string): Promise<Property> {
    return this.request<Property>(`/properties/${id}`);
  }

  async create(propertyData: Partial<Property>): Promise<Property> {
    return this.request<Property>('/properties', {
      method: 'POST',
      body: JSON.stringify(propertyData),
    });
  }

  async update(id: string, propertyData: Partial<Property>): Promise<Property> {
    return this.request<Property>(`/properties/${id}`, {
      method: 'PUT',
      body: JSON.stringify(propertyData),
    });
  }

  async delete(id: string): Promise<void> {
    await this.request<void>(`/properties/${id}`, {
      method: 'DELETE',
    });
  }

  async getStats(): Promise<PropertyStats> {
    return this.request<PropertyStats>('/properties/stats');
  }

  async syncAllSheets(): Promise<{ message: string; count: number }> {
    return this.request<{ message: string; count: number }>('/sync', {
      method: 'POST',
    });
  }

  async syncSheet(sheetName: string): Promise<{ message: string; count: number }> {
    return this.request<{ message: string; count: number }>(`/sync/${sheetName}`, {
      method: 'POST',
    });
  }

  // 🚀 NUEVA FUNCIÓN: Obtener URLs de plataformas inmobiliarias
  async getPlatformUrls(code: string): Promise<{
    success: boolean;
    code: string;
    platform_urls: Record<string, string>;
    summary: {
      total_platforms: number;
      found_count: number;
      success_rate: string;
      best_platform?: string;
      total_time_ms: number;
    };
    timestamp: string;
  }> {
    return this.request(`/platform-urls?code=${encodeURIComponent(code)}`);
  }

  // 🚀 NUEVA FUNCIÓN: Buscar URLs con datos de propiedad completos
  async searchPlatformUrls(propertyData: {
    codigo?: string;
    tipo_inmueble?: string;
    transaccion?: string;
    ciudad?: string;
    sector?: string;
    valor_venta?: number;
    valor_arriendo?: number;
  }): Promise<{
    success: boolean;
    code: string;
    platform_urls: Record<string, string>;
    summary: {
      total_platforms: number;
      found_count: number;
      success_rate: string;
      best_platform?: string;
      total_time_ms: number;
    };
    timestamp: string;
  }> {
    return this.request('/platform-urls', {
      method: 'POST',
      body: JSON.stringify(propertyData),
    });
  }
}

class UserService {
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async getAllUsers(): Promise<User[]> {
    return this.request<User[]>('/users');
  }

  async getUserById(id: string): Promise<User> {
    return this.request<User>(`/users/${id}`);
  }

  async createUser(userData: Partial<User>): Promise<User> {
    return this.request<User>('/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async updateUser(id: string, userData: Partial<User>): Promise<User> {
    return this.request<User>(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  async deleteUser(id: string): Promise<void> {
    await this.request<void>(`/users/${id}`, {
      method: 'DELETE',
    });
  }
}

// Export instances and types
export const propertyService = new PropertyService();
export const userService = new UserService();
export type { User } from '../types/property.types';

export const formatters = {
  currency: (value: number) => 
    new Intl.NumberFormat('es-CO', { 
      style: 'currency', 
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value),
  
  number: (value: number) => 
    new Intl.NumberFormat('es-CO').format(value),
    
  date: (value: string) => 
    new Date(value).toLocaleDateString('es-CO'),
    
  propertyPrice: (property: Property) => {
    if (property.transaccion === 'Venta' && property.valor_venta) {
      return formatters.currency(property.valor_venta);
    } else if (property.transaccion === 'Arriendo' && property.valor_arriendo) {
      return formatters.currency(property.valor_arriendo);
    } else if (property.precio) {
      return formatters.currency(property.precio);
    }
    return 'Precio no disponible';
  }
};